import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardGroup, ChartWidget } from './dashboardSerializer';
import DashboardWidgetCard from './DashboardWidgetCard';
import {
  DashboardDimensionItem,
  DashboardMetricItem,
} from 'domain/payload/response/clientTransmora/projectLayout/ProjectDashboardConfigResp';
import '../templateBuilder/templateBuilder.css';

interface DashboardCanvasPanelProps {
  groups: DashboardGroup[];
  dimensions: DashboardDimensionItem[];
  metrics: DashboardMetricItem[];
  onUpdateGroups: (groups: DashboardGroup[]) => void;
  onEditWidget: (widget: ChartWidget, groupId: string) => void;
  onAddWidgetToGroup: (groupId: string, chartType?: string) => void;
  onLoadSample: () => void;
}

const DashboardCanvasPanel: React.FC<DashboardCanvasPanelProps> = ({
  groups,
  dimensions,
  metrics,
  onUpdateGroups,
  onEditWidget,
  onAddWidgetToGroup,
  onLoadSample,
}) => {
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showNotification = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const handleAddGroup = () => {
    const newGroup: DashboardGroup = {
      id: `group-${Date.now()}`,
      group: [],
    };
    onUpdateGroups([...groups, newGroup]);
    showNotification(`➕ Added new Row ${groups.length + 1}`);
  };

  const handleRemoveGroup = (groupId: string) => {
    if (window.confirm('Are you sure you want to delete this Row?')) {
      onUpdateGroups(groups.filter((g) => g.id !== groupId));
      showNotification(`🗑️ Deleted Row`);
    }
  };

  const handleMoveGroup = (index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= groups.length) return;
    const updated = [...groups];
    const temp = updated[index];
    updated[index] = updated[targetIdx];
    updated[targetIdx] = temp;
    onUpdateGroups(updated);
    showNotification(`↔️ Moved Row ${direction === 'up' ? 'Up' : 'Down'}`);
  };

  const handleRemoveWidget = (widgetId: string, groupId: string) => {
    onUpdateGroups(
      groups.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            group: g.group.filter((w) => w.id !== widgetId),
          };
        }
        return g;
      })
    );
    showNotification(`🗑️ Removed widget from row`);
  };

  const handleUpdateSpan = (groupId: string, widgetId: string, newSpan: number) => {
    onUpdateGroups(
      groups.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            group: g.group.map((w) => {
              if (w.id === widgetId) {
                return { ...w, colSpan: Math.max(1, Math.min(6, newSpan)) };
              }
              return w;
            }),
          };
        }
        return g;
      })
    );
    showNotification(`📐 Resized widget width to ${Math.round((newSpan / 6) * 100)}%`);
  };

  // REORDER WIDGETS ACROSS OR WITHIN ROWS
  const handleReorderWidgets = (
    sourceGroupId: string,
    targetGroupId: string,
    sourceIndex: number,
    targetIndex: number
  ) => {
    let updatedGroups = groups.map((g) => ({
      ...g,
      group: [...g.group],
    }));

    const sourceG = updatedGroups.find((g) => g.id === sourceGroupId);
    const targetG = updatedGroups.find((g) => g.id === targetGroupId);

    if (!sourceG || !targetG) return;

    const [movedWidget] = sourceG.group.splice(sourceIndex, 1);

    let adjustedTargetIndex = targetIndex;
    if (sourceGroupId === targetGroupId && sourceIndex < targetIndex) {
      adjustedTargetIndex = targetIndex - 1;
    }
    adjustedTargetIndex = Math.max(0, Math.min(targetG.group.length, adjustedTargetIndex));

    targetG.group.splice(adjustedTargetIndex, 0, movedWidget);
    onUpdateGroups(updatedGroups);
    showNotification(`✨ Reordered widget "${movedWidget.chartName}"`);
  };

  // INSERT WIDGET FROM SIDEBAR AT INDEX
  const handleInsertWidgetAtIndex = (
    targetGroupId: string,
    targetIndex: number,
    data: any
  ) => {
    let newWidget: ChartWidget | null = null;
    let widgetName = 'Widget';

    if (data.type === 'dashboardChartType') {
      const cType = data.chartType || 'bar';
      widgetName = `${cType.toUpperCase()} Chart`;
      newWidget = {
        id: `widget-${Date.now()}`,
        chartName: widgetName,
        chartType: cType,
        colSpan: 3,
      };
    } else if (data.type === 'dashboardDimension') {
      const foundDim = dimensions.find((d) => d.id === data.dimensionId);
      widgetName = foundDim ? `By ${foundDim.name}` : 'Dimension Chart';
      newWidget = {
        id: `widget-${Date.now()}`,
        chartName: widgetName,
        chartType: 'bar',
        colSpan: 3,
        dimensionKey: data.dimensionId ? [data.dimensionId] : undefined,
      };
    } else if (data.type === 'dashboardMetric') {
      const foundMet = metrics.find((m) => m.id === data.metricId);
      widgetName = foundMet ? foundMet.name : 'Metric Heatmap';
      newWidget = {
        id: `widget-${Date.now()}`,
        chartName: widgetName,
        chartType: 'heatmap',
        colSpan: 3,
        metricKey: data.metricId
          ? [
            {
              metricId: data.metricId,
              metricDataId: foundMet && foundMet.metricData ? foundMet.metricData.map((d) => d.id) : [],
            },
          ]
          : undefined,
      };
    }

    if (newWidget) {
      const created = newWidget;
      onUpdateGroups(
        groups.map((g) => {
          if (g.id === targetGroupId) {
            const newGroupWidgets = [...g.group];
            const safeIdx = Math.max(0, Math.min(newGroupWidgets.length, targetIndex));
            newGroupWidgets.splice(safeIdx, 0, created);
            return { ...g, group: newGroupWidgets };
          }
          return g;
        })
      );
      showNotification(`🚀 Added "${widgetName}" to Row!`);
    }
  };

  // Drag over / drop on group container
  const handleGroupDragOver = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverGroupId(groupId);
  };

  const handleGroupDragLeave = (groupId: string) => {
    if (dragOverGroupId === groupId) {
      setDragOverGroupId(null);
    }
  };

  const handleGroupDrop = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    setDragOverGroupId(null);

    let data = (window as any).__tb_dragged_item;
    if (!data) {
      const dataStr = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
      if (dataStr) {
        try {
          data = JSON.parse(dataStr);
        } catch (err) { }
      }
    }

    if (!data) return;

    try {
      const targetGroup = groups.find((g) => g.id === groupId);
      if (!targetGroup) return;

      if (data.type === 'dashboardWidget') {
        handleReorderWidgets(data.sourceGroupId, groupId, data.sourceIndex, targetGroup.group.length);
      } else {
        handleInsertWidgetAtIndex(groupId, targetGroup.group.length, data);
      }
    } catch (err) {
      console.error('Failed to parse dropped dashboard widget:', err);
    } finally {
      (window as any).__tb_dragged_item = null;
    }
  };

  return (
    <div className="card border shadow-sm h-100 d-flex flex-column tb-canvas-container position-relative">
      {/* TOAST NOTIFICATION BANNER */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="position-absolute start-50 translate-middle-x top-0 mt-2 px-3 py-2 rounded-pill bg-dark text-white shadow-lg d-flex align-items-center gap-2"
            style={{ zIndex: 1000, fontSize: '12px', fontWeight: 600 }}
          >
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CANVAS HEADER */}
      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-shrink-0">
        <h6 className="fw-bold mb-0 text-dark d-flex align-items-center">
          <i className="fas fa-chart-pie text-primary me-2"></i>
          Dashboard Canvas ({groups.length} Rows)
        </h6>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="tb-btn"
            onClick={onLoadSample}
            title="Load Preset Sample Dashboard"
          >
            Load Sample
          </button>
          <button
            type="button"
            className="tb-btn tb-btn-primary fw-semibold px-3"
            onClick={handleAddGroup}
          >
            <i className="fas fa-plus me-1"></i> Add Row
          </button>
        </div>
      </div>

      {/* CANVAS BODY */}
      <div className="card-body p-3 overflow-auto" style={{ height: '560px' }}>
        {groups.length === 0 ? (
          <div className="text-center py-5 border rounded-3 bg-white">
            <i className="fas fa-chart-bar fa-3x text-muted mb-3 opacity-50"></i>
            <h6 className="fw-bold text-dark mb-1">Dashboard Canvas is empty</h6>
            <p className="small text-muted mb-3">
              Add a Row to start adding chart widgets, or click "Load Sample" to load a preset template.
            </p>
            <div className="d-flex justify-content-center gap-2">
              <button
                type="button"
                className="tb-btn tb-btn-primary px-3"
                onClick={handleAddGroup}
              >
                + Add Row
              </button>
              <button
                type="button"
                className="tb-btn px-3"
                onClick={onLoadSample}
              >
                Load Sample
              </button>
            </div>
          </div>
        ) : (
          <AnimatePresence>
            <div className="d-flex flex-column gap-4">
              {groups.map((groupObj, gIdx) => {
                const isDragOver = dragOverGroupId === groupObj.id;
                return (
                  <motion.div
                    key={groupObj.id}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    onDragOver={(e) => handleGroupDragOver(e, groupObj.id)}
                    onDragLeave={() => handleGroupDragLeave(groupObj.id)}
                    onDrop={(e) => handleGroupDrop(e, groupObj.id)}
                    className={`tb-section-card tb-section-dropzone border rounded-3 bg-white shadow-xs overflow-hidden ${isDragOver ? 'is-drag-over' : ''
                      }`}
                  >
                    {/* GROUP HEADER BAR - CLEAN "ROW X" LABEL WITHOUT (X WIDGETS) */}
                    <div className="card-header bg-light border-bottom p-2 px-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <i className="fas fa-layer-group text-primary" style={{ fontSize: '12px' }}></i>
                        <strong className="small text-dark fw-bold" style={{ fontSize: '13px' }}>
                          ROW {gIdx + 1}
                        </strong>
                      </div>

                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon"
                          onClick={() => onAddWidgetToGroup(groupObj.id)}
                          title="Add Chart Widget"
                        >
                          <i className="fas fa-plus text-primary" style={{ fontSize: '11px' }}></i>
                        </button>
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon"
                          disabled={gIdx === 0}
                          onClick={() => handleMoveGroup(gIdx, 'up')}
                          title="Move Row Up"
                        >
                          <i className="fas fa-chevron-up" style={{ fontSize: '11px' }}></i>
                        </button>
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon"
                          disabled={gIdx === groups.length - 1}
                          onClick={() => handleMoveGroup(gIdx, 'down')}
                          title="Move Row Down"
                        >
                          <i className="fas fa-chevron-down" style={{ fontSize: '11px' }}></i>
                        </button>
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon tb-btn-danger ms-1"
                          onClick={() => handleRemoveGroup(groupObj.id)}
                          title="Delete Row"
                        >
                          <i className="fas fa-trash-alt" style={{ fontSize: '11px' }}></i>
                        </button>
                      </div>
                    </div>

                    {/* GROUP BODY - CHARTS ROW */}
                    <div className="card-body p-3">
                      {groupObj.group.length === 0 ? (
                        <div
                          className="text-center py-4 border-2 border-dashed rounded-3 text-muted cursor-pointer"
                          style={{ backgroundColor: '#fcfcfd', borderColor: '#cbd5e1' }}
                          onClick={() => onAddWidgetToGroup(groupObj.id)}
                        >
                          <i className="fas fa-plus-circle text-primary me-1"></i>
                          <span className="small fw-semibold text-primary">
                            Click here or drag from right panel to add a chart widget
                          </span>
                        </div>
                      ) : (
                        <div className="row g-3">
                          {groupObj.group.map((widget, wIdx) => (
                            <DashboardWidgetCard
                              key={widget.id}
                              widget={widget}
                              groupIndex={gIdx}
                              widgetIndex={wIdx}
                              groupId={groupObj.id}
                              dimensions={dimensions}
                              metrics={metrics}
                              onEditWidget={onEditWidget}
                              onRemoveWidget={handleRemoveWidget}
                              onUpdateSpan={handleUpdateSpan}
                              onReorderWidgets={handleReorderWidgets}
                              onInsertWidgetAtIndex={handleInsertWidgetAtIndex}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default DashboardCanvasPanel;
